// ================================
//  App.jsx — PART 1 / 3 (Eksiksiz)
//  - Imports & ChartJS register
//  - normalizeTurkish
//  - Firebase config/init
//  - LABELS & COLORS  (puanlama tabloları src/disc/discScoring.js'e taşındı)
//  - UYAR_REF / DOGAL_REF
//  - interp
//  - i18n (TR + EN)
//  - groups (TR + EN — 24x4 TAM)
//  - letterMapRows
// ================================

import React, { useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import { DISC_RENK } from "./disc/discBrand";
import { letterMapRows } from "./disc/discItems";
import { uyarlanmisSkorlar, dogalSkorlar } from "./disc/discScoring";
import { SinyalPanosu } from "./disc/SinyalSirasi";
import { buildDiscReport } from "./disc/report/discReportEngine";
import { formatDuration } from "./utils/formatDuration";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ClassAnalysis from "./admin/ClassAnalysis";
import { completeInventoryUsage, validateAccessCode, getAccessErrorMessage, DISC_INVENTORY_KEY } from "./access/accessService";
import { kotayiTuketGuvenli } from "./access/kotaTuket";

import { initializeApp } from "firebase/app";
import { getAuth, signInAnonymously } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
  doc,
  setDoc,
} from "firebase/firestore";

// ChartJS register
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// ==================== Türkçe Normalize ====================
function normalizeTurkish(str) {
  return str
    .replace(/ç/g, "c")
    .replace(/Ç/g, "C")
    .replace(/ğ/g, "g")
    .replace(/Ğ/g, "G")
    .replace(/ı/g, "i")
    .replace(/İ/g, "I")
    .replace(/ö/g, "o")
    .replace(/Ö/g, "O")
    .replace(/ş/g, "s")
    .replace(/Ş/g, "S")
    .replace(/ü/g, "u")
    .replace(/Ü/g, "U");
}

// ==================== Firebase ====================
const firebaseConfig = {
  apiKey: "AIzaSyDKpuzGgxlZQH84XrjRBaPZN3B8d7IVwAk",
  authDomain: "somerenlabs-inv2.firebaseapp.com",
  projectId: "somerenlabs-inv2",
  storageBucket: "somerenlabs-inv2.firebasestorage.app",
  messagingSenderId: "1063728012080",
  appId: "1:1063728012080:web:57a1ba253d218400e446f5",
};
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

// ── KATMAN A · ANONİM OTURUM ────────────────────────────────────────────────
// Firestore kuralları `request.auth != null` istiyor; oturum olmadan bu uygulama
// ne yazabilir ne okuyabilir.
//
// DİKKAT — bu kod NEDEN burada, src/firebase.js'te değil:
// disc-app'te src/firebase.js var ama HİÇBİR YERDEN import EDİLMİYOR (ölü dosya);
// gerçek Firebase örneği yukarıda, App.js içinde başlatılıyor. Anonim oturum
// 2026-08-09'da yanlışlıkla o ölü dosyaya eklenmişti, bu yüzden canlıda hiç
// çalışmadı ve uygulama yeni DISC sonucu kaydedemez hâle geldi (2026-08-10'da
// tarayıcıda tespit edildi: firebaseLocalStorageDb oluşmuyordu).
// Buraya taşındı; ölü dosyaya bakıp "eklenmiş" sanma.
export const authHazir = signInAnonymously(getAuth(app))
  .catch((err) => { console.error("Anonim oturum açılamadı:", err); });

function getSomerenAccessParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    accessCode: String(params.get("slAccessCode") || "").trim().toUpperCase(),
    inventoryKey: String(params.get("slInventory") || "").trim().toUpperCase(),
    sourceLang: String(params.get("slLang") || "").trim().toLowerCase(),
  };
}

// ==================== LABELS & COLORS ====================
const LABELS = ["D", "I", "S", "C"];
// Katılımcının test sonu grafikleri de paylaşım belgesinin paletini kullanır;
// rapor ve admin ile aynı olsun diye tek kaynaktan okunur (src/disc/discBrand.js).
const COLORS = DISC_RENK;

// ==================== ANALYSIS REFERENCE TABLES ====================


// ==================== interpolasyon ====================
function interp(mapObj, x) {
  const keys = Object.keys(mapObj).map(Number).sort((a, b) => a - b);
  if (x <= keys[0]) return mapObj[keys[0]];
  if (x >= keys[keys.length - 1]) return mapObj[keys[keys.length - 1]];
  for (let i = 0; i < keys.length - 1; i++) {
    const x0 = keys[i], x1 = keys[i + 1];
    if (x >= x0 && x <= x1) {
      const y0 = mapObj[x0], y1 = mapObj[x1];
      const t = (x - x0) / (x1 - x0);
      return Math.round(y0 + t * (y1 - y0));
    }
  }
  return 0;
}

// ==================== i18n (TR + EN) ====================
const i18n = {
  tr: {
    welcome: "DISC Envanterine Hoş Geldiniz",
    chooseLang: "Lütfen dil seçin",
    durationNote: "⏱ Yetişkinler bu envanteri genelde 8-10 dakikada tamamlar.",
    durationLabel: "Süre",
    warningNote: "Dikkat: En gerçekçi sonuç için ara vermeden tek oturumda tamamlamanızı ve aklınıza ilk gelen cevaplarla ilerlemenizi öneriyoruz.",
    name: "Ad",
    surname: "Soyad",
    start: "Başla",
    adminPanel: "Admin Paneli",
    adminBack: "← Uygulamaya Dön",
    instructions: "Yönergeler",
    instructionText:
      "Her grupta 4 satır göreceksiniz. Sizi en iyi tanımlayanı En Çok, en az tanımlayanı En Az olarak seçiniz.",
    beginInventory: "Envantere Başla",
    group: "Grup",
    most: "En Çok",
    least: "En Az",
    next: "Sonraki →",
    seeResults: "Sonuçları Gör",
    progressSep: "/",
    resultsTitle: "DISC Sonuçlarınız",
    adaptedTitle: "Uyarlanmış (Çevre Etkili)",
    naturalTitle: "Doğal",
    nameRequired: "Lütfen ad ve soyad giriniz.",
    duplicateConfirm: "Bu isimle kayıt var. Yeniden yapmak istiyor musunuz?",
    checking: "Kontrol ediliyor...",
    wrongPwd: "Yanlış şifre!",
    mustSelect: "Lütfen her grupta bir En Çok ve bir En Az seçiniz.",
  },
  en: {
    welcome: "Welcome to the DISC Inventory",
    chooseLang: "Please choose your language",
    durationNote: "⏱ Adults usually complete this inventory in about 8–10 minutes.",
    durationLabel: "Duration",
    warningNote: "Please note: For the most accurate results, we recommend completing the inventory in a single uninterrupted session and going with your first, instinctive responses.",
    name: "Name",
    surname: "Surname",
    start: "Start",
    adminPanel: "Admin Panel",
    adminBack: "← Back to App",
    instructions: "Instructions",
    instructionText:
      "In each group you will see 4 statements. Choose the one that describes you the MOST and the one that describes you the LEAST.",
    beginInventory: "Start the Inventory",
    group: "Group",
    most: "Most",
    least: "Least",
    next: "Next →",
    seeResults: "See Results",
    progressSep: "/",
    resultsTitle: "Your DISC Results",
    adaptedTitle: "Adapted (Environment-Influenced)",
    naturalTitle: "Natural",
    nameRequired: "Please enter name and surname.",
    duplicateConfirm: "A record exists with this name. Do you want to retake?",
    checking: "Checking...",
    wrongPwd: "Wrong password!",
    mustSelect: "Please select one Most and one Least in each group.",
  },
};

// ==================== Gruplar (TR + EN) — 24x4 TAM ====================
const groups = {
  tr: [
    { id: 1, rows: ["Nazik, kibar", "İnandırıcı, ikna edici", "Mütevazi, tutucu, sade", "Orjinal, yaratıcı, bireyci"] },
    { id: 2, rows: ["Çekici, cazibeli", "İşbirlikçi, uyumlu", "İnatçı, boyun eğmeyen", "Tatlı, hoşa giden"] },
    { id: 3, rows: ["Kolay yönlendirilen, takipçi", "Küstah, cüretkar", "Sadık, bağlı, kendini adayan", "Çekici, coşkulu"] },
    { id: 4, rows: ["Açık-fikirli, yeni fikirlere açık", "Yardım etmeye hazır", "Güçlü iradeye sahip, iradeli", "Neşeli, mutlu"] },
    { id: 5, rows: ["İçten, şakacı", "Kesin, tam, kati", "Gergin, cesur, pişkin", "Dengeli, sakin, kolay heyecanlanmayan"] },
    { id: 6, rows: ["Rekabetçi, kazanmak için uğraşan", "Düşünceli, anlayışlı, şefkatli", "Dışadönük, eğlence-seven, sosyal", "Uyumlu, kolay anlaşabilen"] },
    { id: 7, rows: ["Yaygaracı, zor beğenen", "Yumuşak başlı, görevine bağlı", "Yenilmez, kararlı", "Oyuncu, maskara, eğlence dolu"] },
    { id: 8, rows: ["Cesur, korkmayan, gözüpek", "İlham veren, motive eden", "Uysal, söz dinleyen, taviz veren", "Sıkılgan, mahcup, sessiz"] },
    { id: 9, rows: ["Sosyal, diğerleriyle olmaktan hoşlanan", "Sabırlı, sebatkar, hoşgörülü", "Kendi başına ayakta duran, bağımsız", "Tatlı dilli, ılımlı, tutucu"] },
    { id: 10, rows: ["Maceraperest, şansını kullanan", "Yeni fikirlere açık, önerilere açık", "Sıcak kanlı, dost, yakın", "Ölçülü, makul, uçlarda gezinmeyen"] },
    { id: 11, rows: ["Konuşkan, geveze", "Kontrollü, duygularını bastıran", "Geleneksel, kabul edilmiş yolları seçen", "Kararlı, kesin, kararlarında kati"] },
    { id: 12, rows: ["Kibar, tatlı ve inandırıcı konuşan", "Cesur, cüretkar, risk-alan", "Diplomatik, insanlara karşı düşünceli", "Tatmin olmuş, mutlu, hoşnut"] },
    { id: 13, rows: ["Agresif, zorlayıcı işlerden hoşlanan", "Dışadönük, partilerin göz bebeği", "Kolay ikna edilebilen", "Korku dolu, ürkmüş"] },
    { id: 14, rows: ["Temkinli, ihtiyatlı, dikkatli", "Kararlı, kati, sarsılmaz", "İkna edici, inandırıcı", "İyi huylu, hoş"] },
    { id: 15, rows: ["İstekli, hazır, gönüllü", "İstekli, heyecanlı, huzursuz", "Uyumlu, kabul eden", "Neşeli, canlı, çoşkulu"] },
    { id: 16, rows: ["Kendinden emin, kendine güvenen", "Merhametli, şefkatli, anlayışlı", "Hoşgörülü", "Kararlı, agresif"] },
    { id: 17, rows: ["Disiplinli, kontrollü", "Cömert, paylaşmaya istekli", "Canlı, hayat dolu, hareketli", "Kararlı, başeğmeyen, vazgeçmeyen"] },
    { id: 18, rows: ["Beğenilen, takdire değer", "Kibar, yardımcı olmaya istekli", "Mütevekkil(kabullenen), işi oluruna bırakan", "Güçlü kişilikli"] },
    { id: 19, rows: ["Saygılı, saygı gösteren", "Öncü, araştırmacı, girişimci", "İyimser, olumlu görüş açısına sahip", "Uyum sağlayabilen, yardıma hazır"] },
    { id: 20, rows: ["Tartışma meraklısı, ağız dalaşçısı", "Uyum sağlayan, esnek", "İlgisiz, umursamaz, soğukkanlı", "Kaygısız, neşeli, tasasız"] },
    { id: 21, rows: ["Güvenen, diğerlerine bağlı", "Hoşnut, mutlu, tatmin olmuş", "Olumlu, kuşku taşımayan", "Barışçı, sakin"] },
    { id: 22, rows: ["Diğerleriyle olmaktan hoşlanan", "Kültürlü, eğitimli, bilgili", "Faal, enerjik, dinç", "Hoşgörülü, nazik, merhametli"] },
    { id: 23, rows: ["Arkadaş canlısı, samimi", "Doğru, tam, kesin", "Açık sözlü, patavatsızca konuşabilen", "Duygularını bastıran, kontrollü"] },
    { id: 24, rows: ["Huzursuz, gergin, yerinde duramayan", "Dostça, arkadaşça", "Popüler, çoğu tarafından sevilen", "Düzenli, tertipli, temiz"] },
  ],
  en: [
    { id: 1, rows: ["Kind, polite", "Convincing, persuasive", "Modest, conservative, plain", "Original, creative, individualistic"] },
    { id: 2, rows: ["Attractive, charming", "Cooperative, agreeable", "Stubborn, unyielding", "Sweet, pleasant"] },
    { id: 3, rows: ["Easily led, a follower", "Arrogant, audacious", "Loyal, devoted, dedicated", "Attractive, enthusiastic"] },
    { id: 4, rows: ["Open-minded, receptive to new ideas", "Ready to help", "Strong-willed, determined", "Cheerful, happy"] },
    { id: 5, rows: ["Sincere, playful", "Exact, precise", "Tense, bold, brash", "Balanced, calm, not easily excited"] },
    { id: 6, rows: ["Competitive, driven to win", "Considerate, caring, thoughtful", "Outgoing, fun-loving, social", "Agreeable, easy to get along with"] },
    { id: 7, rows: ["Noisy, hard to please", "Soft-spoken, dutiful", "Unbeatable, resolute", "Playful, clownish, full of fun"] },
    { id: 8, rows: ["Brave, fearless, daring", "Inspiring, motivating", "Docile, compliant, yielding", "Shy, timid, quiet"] },
    { id: 9, rows: ["Sociable, enjoys being with others", "Patient, persevering, tolerant", "Self-reliant, independent", "Sweet-tongued, moderate, conservative"] },
    { id: 10, rows: ["Adventurous, takes chances", "Open to new ideas and suggestions", "Warm, friendly, close", "Measured, reasonable, avoids extremes"] },
    { id: 11, rows: ["Talkative, chatty", "Controlled, suppresses emotions", "Traditional, chooses accepted ways", "Decisive, firm, resolute"] },
    { id: 12, rows: ["Polite, sweet and persuasive speaker", "Brave, daring, risk-taking", "Diplomatic, considerate toward people", "Satisfied, content, pleased"] },
    { id: 13, rows: ["Aggressive, enjoys demanding tasks", "Extroverted, life of the party", "Easily convinced", "Fearful, scared"] },
    { id: 14, rows: ["Cautious, prudent, careful", "Determined, firm, unwavering", "Persuasive, convincing", "Good-natured, pleasant"] },
    { id: 15, rows: ["Willing, ready, eager", "Willing, excited, restless", "Agreeable, accepting", "Cheerful, lively, enthusiastic"] },
    { id: 16, rows: ["Self-confident", "Compassionate, merciful, understanding", "Tolerant", "Resolute, aggressive"] },
    { id: 17, rows: ["Disciplined, self-controlled", "Generous, willing to share", "Lively, full of life, active", "Determined, unbowing, persistent"] },
    { id: 18, rows: ["Admirable, worthy of appreciation", "Polite, willing to help", "Resigned, lets things take their course", "Strong-personality"] },
    { id: 19, rows: ["Respectful, shows respect", "Pioneer, explorer, enterprising", "Optimistic, positive outlook", "Adaptable, ready to help"] },
    { id: 20, rows: ["Argumentative, quarrelsome", "Adaptable, flexible", "Indifferent, unconcerned, cool-headed", "Carefree, cheerful, untroubled"] },
    { id: 21, rows: ["Trusting, dependent on others", "Content, happy, satisfied", "Positive, free of doubt", "Peaceful, calm"] },
    { id: 22, rows: ["Enjoys being with others", "Cultured, educated, knowledgeable", "Active, energetic, vigorous", "Tolerant, kind, compassionate"] },
    { id: 23, rows: ["Friendly, sincere", "Right, exact, precise", "Frank, blunt, outspoken", "Suppresses emotions, controlled"] },
    { id: 24, rows: ["Restless, tense, fidgety", "Friendly, amicable", "Popular, liked by most", "Orderly, neat, tidy"] },
  ],
};

// ==================== MOST/LEAST HARF HARİTASI ====================
// letterMapRows ortak modüle taşındı: src/disc/discItems.js
// ================================
//  App.jsx — PART 2 / 3 (Eksiksiz)
//  - App component açılış
//  - state & useEffect
//  - seçim yardımcıları (setRowSelection, isRowSelected)
//  - buildCounts
//  - chartOptions
//  - handleSubmit (Firestore yazma dahil)
// ================================

export default function App() {
  const ADMIN_PASSWORD = "ajl6138";

  // ---- Dil ve çeviri ----
  const [lang, setLang] = useState("tr");
  const t = i18n[lang];

  // ---- Admin durumu ----
  const [adminLogin, setAdminLogin] = useState(false);
  // 2026-08-21: admin şifresi tarayıcının prompt() kutusuyla soruluyordu ve oraya
  // dil seçici konulamıyordu. Sayfa içi panele çevrildi: hem şifre hem dil burada.
  const [adminPanelAcik, setAdminPanelAcik] = useState(false);
  const [adminSifreGirdi, setAdminSifreGirdi] = useState("");
  const [adminSifreHata, setAdminSifreHata] = useState("");
  const [adminMode, setAdminMode] = useState(false);

  // ---- Someren Labs erişim kodu koruması ----
  const [inventoryAccessGranted, setInventoryAccessGranted] = useState(false);
  const [inventoryPasswordInput, setInventoryPasswordInput] = useState("");
  const [inventoryPasswordError, setInventoryPasswordError] = useState("");
  const [inventoryAccessLoading, setInventoryAccessLoading] = useState(false);
  const [somerenAccessContext, setSomerenAccessContext] = useState(null);

  // ---- Akış durumları ----
  // step: 0 = dil seçimi, 1 = ad-soyad, 2 = test (24x4), 3 = sonuçlar
  const [step, setStep] = useState(0);
  const [user, setUser] = useState({
    name: "",
    surname: "",
    generationRange: "",
    roleLevel: "",
    kvkkConsent: false
  });
  const [answers, setAnswers] = useState([]); // [{groupId, most, least}]
  const [charts, setCharts] = useState(null);
  const [timestamp, setTimestamp] = useState("");
  const [currentGroup, setCurrentGroup] = useState(0);
  const [checkingUser, setCheckingUser] = useState(false);

  // ---- Süre takibi: test (step 2) başladığı an -> bitiş ----
  const testStartRef = useRef(null);
  const [durationMs, setDurationMs] = useState(null);

  useEffect(() => {
    if (step === 2 && testStartRef.current == null) {
      testStartRef.current = Date.now();
    }
  }, [step]);

  // ---- Responsive / mobil grafik kare görünümü stabilizasyonu ----
  useEffect(() => {
    const r = () => document.body.style.setProperty("--v", String(Math.random()));
    window.addEventListener("resize", r);
    // ilk mount sonrası ufak tetikleyiciler (Chart.js canvas ölçüsü için)
    setTimeout(r, 50);
    setTimeout(r, 250);
    return () => window.removeEventListener("resize", r);
  }, []);

  // ---- Someren Labs access gate ----
  useEffect(() => {
    const somerenAccess = getSomerenAccessParams();

    if (somerenAccess.sourceLang === "en" || somerenAccess.sourceLang === "tr") {
      setLang(somerenAccess.sourceLang);
    }

    if (somerenAccess.accessCode && somerenAccess.inventoryKey === DISC_INVENTORY_KEY) {
      setInventoryAccessLoading(true);
      validateAccessCode(somerenAccess.accessCode, DISC_INVENTORY_KEY)
        .then((access) => {
          setSomerenAccessContext(access);
          setInventoryAccessGranted(true);
          setInventoryPasswordError("");
          setInventoryPasswordInput(somerenAccess.accessCode);
        })
        .catch((error) => {
          setInventoryAccessGranted(false);
          setInventoryPasswordError(getAccessErrorMessage(error, somerenAccess.sourceLang || "tr"));
        })
        .finally(() => setInventoryAccessLoading(false));
    }
  }, []);

  // ==================== SEÇİM YÖNETİMİ ====================
  // Bir grupta MOST/LEAST seçimini ayarla; çakışma varsa diğerini boşalt
  const setRowSelection = (groupId, type, rowIndex) => {
    setAnswers((prev) => {
      const updated = prev.filter((p) => p.groupId !== groupId);
      const current = prev.find((p) => p.groupId === groupId) || { groupId };

      if (type === "most" && current.least === rowIndex) current.least = null;
      if (type === "least" && current.most === rowIndex) current.most = null;

      updated.push({ ...current, [type]: rowIndex });
      return updated;
    });
  };

  const isRowSelected = (groupId, type, rowIndex) => {
    const g = answers.find((a) => a.groupId === groupId);
    return g && g[type] === rowIndex;
  };

  // ==================== SAYIM OLUŞTURMA (MOST/LEAST → D/I/S/C) ====================
  const buildCounts = () => {
    const most = { D: 0, I: 0, S: 0, C: 0 };
    const least = { D: 0, I: 0, S: 0, C: 0 };

    answers.forEach((a) => {
      const gm = letterMapRows[a.groupId - 1];
      if (!gm) return;

      const mostLetter = gm[a.most]?.[0] ?? "-";
      const leastLetter = gm[a.least]?.[1] ?? "-";

      if (most[mostLetter] !== undefined) most[mostLetter]++;
      if (least[leastLetter] !== undefined) least[leastLetter]++;
    });

    return { most, least };
  };

  // ==================== GRAFİK OPSİYONLARI (kare görünüm + % tooltip) ====================
  const chartOptions = (title, { reverseY = false } = {}) => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        min: 0,
        max: 100,
        reverse: reverseY,
        ticks: { callback: (v) => v + "%" },
        grid: {
          color: (ctx) => (ctx.tick.value === 50 ? "#000" : "rgba(0,0,0,0.1)"),
          lineWidth: (ctx) => (ctx.tick.value === 50 ? 1.2 : 0.5),
        },
      },
      x: { grid: { display: false } },
    },
    plugins: {
      legend: { display: false },
      title: { display: true, text: title, font: { size: 16 } },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
        },
      },
    },
  });

  // ==================== SONUÇ HESAPLAMA + FIRESTORE KAYDET ====================
  // 2026-08-16 · ÇİFT KAYIT ONARIMI
  // Sorun: "Sonuçları Gör" düğmesinin koruması yoktu. Kayıt `await` ile yazılıyor,
  // ekran ancak ondan SONRA değişiyordu; arada kalan ~1 saniyede ikinci tıklama
  // YENİ rastgele kimlikli İKİNCİ belge oluşturuyordu. (Figen Müge Guzel için
  // 16.08.2026 18:13:17 ve 18:13:18'de iki kayıt böyle oluştu.)
  // Kota iki kez düşmüyordu — tek kullanımlık kod ikinci denemeyi reddediyor — ama
  // sonuç belgesi lisans adımından ÖNCE yazıldığı için ortada kalıyordu.
  // Çözüm: ref ile anlık kilit (state gecikmesine güvenilmez) + düğme devre dışı.
  const gonderiliyorRef = useRef(false);
  const [gonderiliyor, setGonderiliyor] = useState(false);

  const handleSubmit = async () => {
    if (gonderiliyorRef.current) return;   // ikinci tıklama sessizce yok sayılır
    gonderiliyorRef.current = true;
    setGonderiliyor(true);
    let kayitYazildi = false;
    const { most, least } = buildCounts();

    // Süre: test başlangıcından bitişe
    const rawDurationMs = testStartRef.current ? Date.now() - testStartRef.current : null;
    const safeDurationMs =
      Number.isFinite(Number(rawDurationMs)) && Number(rawDurationMs) > 0
        ? Math.round(Number(rawDurationMs))
        : null;
    setDurationMs(safeDurationMs);

    // Referans tablolarla %'lere çevir
    // Puanlama ölçütü: DISC analizi envanter ŞABLON.xlsx (src/disc/discScoring.js).
    // Buradaki yerel UYAR_REF/DOGAL_REF tabloları şablondan farklıydı; artık
    // kullanılmıyor — tek kaynak discScoring modülüdür.
    const uyarSk = uyarlanmisSkorlar(most);
    const dogalSk = dogalSkorlar(least);
    const mostPerc = LABELS.map((L) => uyarSk[L]);
    const leastPerc = LABELS.map((L) => dogalSk[L]);

    const adaptedScoresForReport = Object.fromEntries(
      LABELS.map((label, index) => [label, Math.round(mostPerc[index] ?? 0)])
    );

    const naturalScoresForReport = Object.fromEntries(
      LABELS.map((label, index) => [label, Math.round(leastPerc[index] ?? 0)])
    );

    const discReport = buildDiscReport({
      adaptedScores: adaptedScoresForReport,
      naturalScores: naturalScoresForReport,
      participant: {
        name: [user.name, user.surname].filter(Boolean).join(" "),
        company: somerenAccessContext?.companyName || "",
        team: somerenAccessContext?.teamName || "",
        generationRange: user.generationRange || "",
        roleLevel: user.roleLevel || ""
      },
      language: lang
    });

    const compactReportSummary = {
      title: discReport.title,
      primaryStyle: discReport.primary,
      secondaryStyle: discReport.secondary,
      lowestStyle: discReport.lowest,
      naturalPrimaryStyle: discReport.naturalPrimary,
      adaptedScores: discReport.adapted,
      naturalScores: discReport.natural,
      deltas: discReport.deltas,
      executiveSummary: discReport.executiveSummary,
      adaptationInsight: discReport.adaptationInsight,
      generatedAt: new Date().toISOString()
    };

    // Grafik datasetleri
    setCharts({
      mostData: {
        labels: LABELS,
        datasets: [
          {
            label: lang === "tr" ? "Uyarlanmış" : "Adapted",
            data: mostPerc,
            backgroundColor: LABELS.map((l) => COLORS[l]),
          },
        ],
      },
      leastData: {
        labels: LABELS,
        datasets: [
          {
            label: lang === "tr" ? "Doğal" : "Natural",
            data: leastPerc,
            backgroundColor: LABELS.map((l) => COLORS[l]),
          },
        ],
      },
    });

    // Zaman damgası (locale'a göre)
    setTimestamp(new Date().toLocaleString(lang === "tr" ? "tr-TR" : "en-US"));

    // Firestore kaydı
    try {
      // ID üretimi: Firebase console'da isim-soyisimle görünsün diye okunabilir.
      // "Ad Soyad — 2026-06-25 1430 a1b2" (tarih + kısa rastgele ek = çakışma önler).
      const displayName = [user.name, user.surname].filter(Boolean).join(" ").trim() || "Anonim";
      const dn = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const idStamp = `${dn.getFullYear()}-${pad(dn.getMonth() + 1)}-${pad(dn.getDate())} ${pad(dn.getHours())}${pad(dn.getMinutes())}`;
      const idRand = Math.random().toString(36).slice(2, 6);
      const docId = `${displayName} — ${idStamp} ${idRand}`
        .replace(/\//g, "-")
        .replace(/^\.+|\.+$/g, "")
        .slice(0, 200);

      const urlSomerenAccess = getSomerenAccessParams();
      const somerenAccess = {
        accessCode: urlSomerenAccess.accessCode || somerenAccessContext?.accessCode || "",
        inventoryKey: urlSomerenAccess.inventoryKey || somerenAccessContext?.inventoryKey || DISC_INVENTORY_KEY,
        sourceLang: urlSomerenAccess.sourceLang || lang,
        companyId: somerenAccessContext?.companyId || "",
        companyName: somerenAccessContext?.companyName || "",
        teamId: somerenAccessContext?.teamId || "",
        teamName: somerenAccessContext?.teamName || "",
      };

      await setDoc(doc(db, "disc_sonuclari", docId), {
        lang,
        name: user.name,
        surname: user.surname,
        timestamp: serverTimestamp(),
        timestampString: new Date().toLocaleString(lang === "tr" ? "tr-TR" : "en-US"),
        mostCounts: most,
        leastCounts: least,
        answers,
        participant: {
          firstNameOrAlias: user.name || "",
          lastName: user.surname || "",
          generationRange: user.generationRange || "",
          roleLevel: user.roleLevel || "",
          kvkkConsent: Boolean(user.kvkkConsent),
          kvkkConsentAt: new Date().toISOString()
        },
        reportSummary: compactReportSummary,
        somerenAccess,
        accessCode: somerenAccess.accessCode,
        inventoryKey: somerenAccess.inventoryKey,
        sourceLang: somerenAccess.sourceLang,
        durationMs: safeDurationMs,
        durationSeconds: safeDurationMs != null ? Math.round(safeDurationMs / 1000) : null,
      });
      kayitYazildi = true;   // bundan sonra hata olsa da kilidi AÇMA: belge zaten var

      if (somerenAccess.accessCode && somerenAccess.inventoryKey) {
        // Kota tüketimi çakışma yüzünden düşebiliyor (tüm tüketimler tek lisans
        // belgesinin sayaçlarını günceller). Eskiden hata yutuluyordu: kayıt
        // kalıyor, kota düşmüyordu. Şimdi yeniden denenir; yine olmazsa kayıt
        // `quotaPending` ile işaretlenir ki mutabakat yapılabilsin.
        const kotaSonucu = await kotayiTuketGuvenli(
          () => completeInventoryUsage({
          accessCode: somerenAccess.accessCode,
          inventoryKey: somerenAccess.inventoryKey,
          resultId: docId,
          participantName: `${user.name} ${user.surname}`.trim(),
          language: lang,
        })
        );
      if (!kotaSonucu.ok) {
        console.warn(
          "[kota] DISC: kayıt yazıldı ama kota düşmedi — quotaPending işaretlendi"
        );
      }
      }
    } catch (err) {
      if (!kayitYazildi) {
        gonderiliyorRef.current = false;   // belge yazılamadı → tekrar denenebilsin
        setGonderiliyor(false);
      }
      alert((lang === "tr" ? "Sonuç kaydedilirken hata: " : "Error saving result: ") + err.message);
    }

    setGonderiliyor(false);
    setStep(3);
  };

  // ---- Ekranlar & Admin render'ı Part 3'te (erken return'lerden önce tüm fonksiyonlar tanımlı) ----
  // (Part 3: step===0/1/2/3 + admin mode render blokları)
// ================================
//  App.jsx — PART 3 / 3 (Eksiksiz)
//  - step 0/1/2/3 ekranları
//  - admin panel render
// ================================

// ==================== ADMİN PANEL RENDER ====================
// ==================== ADMİN GİRİŞİ — şifre + dil ====================
// Ekranda hem şifre hem dil seçimi vardır. Dil, admin ARAYÜZÜNÜN dilidir;
// raporun dili ayrıca ClassAnalysis içinden seçilir (varsayılan: kaydın kendi dili).
if (adminPanelAcik && !adminLogin) {
  const ag = lang === "tr";
  const dilDugmesi = (kod, etiket) => (
    <button
      key={kod}
      onClick={() => setLang(kod)}
      style={{
        padding: "8px 18px",
        borderRadius: 8,
        cursor: "pointer",
        fontWeight: 700,
        fontSize: 14,
        border: lang === kod ? "2px solid #0e2a3f" : "1px solid #d1d5db",
        background: lang === kod ? "#0e2a3f" : "#fff",
        color: lang === kod ? "#fff" : "#374151",
      }}
    >
      {etiket}
    </button>
  );
  const dene = () => {
    if (adminSifreGirdi === ADMIN_PASSWORD) {
      setAdminLogin(true);
      setAdminMode(true);
      setAdminPanelAcik(false);
      setAdminSifreHata("");
    } else {
      setAdminSifreHata(ag ? "Yanlış şifre." : "Wrong password.");
    }
  };
  return (
    <div style={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 380, border: "1px solid #e3e7ec", borderRadius: 16, padding: 28, background: "#fff" }}>
        <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: ".1em", color: "#5b6473" }}>SOMEREN LABS · DISC</p>
        <h2 style={{ margin: "8px 0 4px", fontSize: 20, color: "#0e2a3f" }}>{ag ? "Admin Girişi" : "Admin Login"}</h2>
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#6b7280" }}>
          {ag ? "Bu ekran Someren Labs ekibi içindir." : "This screen is for the Someren Labs team."}
        </p>

        <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
          {ag ? "Arayüz dili" : "Interface language"}
        </label>
        <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          {dilDugmesi("tr", "Türkçe")}
          {dilDugmesi("en", "English")}
        </div>

        <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
          {ag ? "Şifre" : "Password"}
        </label>
        <input
          type="password"
          autoFocus
          value={adminSifreGirdi}
          onChange={(e) => { setAdminSifreGirdi(e.target.value); setAdminSifreHata(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") dene(); }}
          style={{ width: "100%", boxSizing: "border-box", padding: 11, borderRadius: 8, border: "1px solid #d1d5db", fontSize: 15 }}
        />
        {adminSifreHata ? (
          <div style={{ marginTop: 8, color: "#b91c1c", fontSize: 13, fontWeight: 600 }}>{adminSifreHata}</div>
        ) : null}

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button
            onClick={dene}
            style={{ flex: 1, padding: "12px 18px", background: "#0e2a3f", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: "pointer" }}
          >
            {ag ? "Gir" : "Enter"}
          </button>
          <button
            onClick={() => { setAdminPanelAcik(false); setAdminSifreGirdi(""); setAdminSifreHata(""); }}
            style={{ padding: "12px 18px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, fontWeight: 600, fontSize: 15, cursor: "pointer" }}
          >
            {ag ? "Vazgeç" : "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}

if (adminMode && adminLogin) {
  return (
    <div style={{ padding: 12 }}>
      <button
        onClick={() => {
          setAdminMode(false);
          setAdminLogin(false);
        }}
        style={{
          marginBottom: 12,
          padding: "8px 12px",
          borderRadius: 8,
          border: "1px solid #ccc",
          cursor: "pointer",
          background: "#fff",
        }}
      >
        {t.adminBack}
      </button>

      {/* Admin arayüz dili — panelin içinden de değiştirilebilir */}
      <span style={{ marginLeft: 10, marginRight: 4, fontSize: 12, color: "#6b7280" }}>
        {lang === "tr" ? "Arayüz dili" : "Interface language"}
      </span>
      <select
        value={lang}
        onChange={(e) => setLang(e.target.value)}
        style={{ marginBottom: 12, padding: "7px 10px", borderRadius: 8, border: "1px solid #ccc", fontSize: 14, cursor: "pointer" }}
      >
        <option value="tr">Türkçe</option>
        <option value="en">English</option>
      </select>

      {/* DISC İletişim Rehberi — şifre girildikten sonra indirilebilir (2026-08-20) */}
      <a
        href={`${process.env.PUBLIC_URL}/${
          lang === "tr"
            ? "SomerenLabs_DISC_Iletisim_Rehberi.pdf"
            : "SomerenLabs_DISC_Communication_Guide.pdf"
        }`}
        download={lang === "tr"
          ? "SomerenLabs_DISC_Iletisim_Rehberi.pdf"
          : "SomerenLabs_DISC_Communication_Guide.pdf"}
        style={{
          marginBottom: 12,
          marginLeft: 8,
          padding: "8px 14px",
          borderRadius: 8,
          border: "1px solid #0e2a3f",
          background: "#0e2a3f",
          color: "#fff",
          fontWeight: 700,
          fontSize: 14,
          textDecoration: "none",
          display: "inline-block",
        }}
      >
        {lang === "tr"
          ? "\u2193 DISC İletişim Rehberi (PDF)"
          : "\u2193 DISC Communication Guide (PDF)"}
      </a>

      {/* Tek sayfalık DISC kartı — iki dilli (2026-08-21) */}
      <a
        href={`${process.env.PUBLIC_URL}/${
          lang === "tr" ? "SomerenLabs_DISC_Karti.pdf" : "SomerenLabs_DISC_Card.pdf"
        }`}
        download={lang === "tr" ? "SomerenLabs_DISC_Karti.pdf" : "SomerenLabs_DISC_Card.pdf"}
        title={lang === "tr"
          ? "Tek sayfalık kart: dört çeyrek, eksenler ve alıcıya göre iletişim. İki dilde."
          : "One-page card: four quadrants, the axes, and how to shape the message. In both languages."}
        style={{
          marginBottom: 12,
          marginLeft: 8,
          padding: "8px 14px",
          borderRadius: 8,
          border: "1px solid #2fb8c6",
          background: "#fff",
          color: "#0e2a3f",
          fontWeight: 700,
          fontSize: 14,
          textDecoration: "none",
          display: "inline-block",
        }}
      >
        {lang === "tr" ? "\u2193 DISC Kartı (PDF)" : "\u2193 DISC Card (PDF)"}
      </a>

      <ClassAnalysis lang={lang} />
    </div>
  );
}

// ==================== ENVANTER ŞİFRE KONTROLÜ ====================
if (!inventoryAccessGranted) {
  return (
    <div style={{ textAlign: "center", marginTop: 40 }}>
      <h2>{lang === "tr" ? "DISC Envanteri Erişimi" : "DISC Inventory Access"}</h2>
      <p>{lang === "tr" ? "Bu envanteri açmak için şirket, takım veya katılımcı erişim kodunuzu girin." : "Please enter your company, team or participant access code to open this inventory."}</p>
      <p style={{ fontSize: 13, opacity: 0.7, marginTop: 6 }}>{t.durationNote}</p>

      <input
        type="text"
        placeholder={lang === "tr" ? "Erişim kodu" : "Access code"}
        value={inventoryPasswordInput}
        onChange={(e) => setInventoryPasswordInput(e.target.value)}
        style={{ padding: 10, marginTop: 10, width: "80%", maxWidth: 420 }}
      />

      {inventoryPasswordError && (
        <p style={{ color: "red", marginTop: 8 }}>{inventoryPasswordError}</p>
      )}

      <br />

      <button
        onClick={async () => {
          setInventoryAccessLoading(true);
          setInventoryPasswordError("");

          try {
            const access = await validateAccessCode(inventoryPasswordInput, DISC_INVENTORY_KEY);
            setSomerenAccessContext(access);
            setInventoryAccessGranted(true);
          } catch (error) {
            setInventoryPasswordError(getAccessErrorMessage(error, lang));
          } finally {
            setInventoryAccessLoading(false);
          }
        }}
        style={{
          marginTop: 16,
          padding: "12px 20px",
          backgroundColor: "#1976d2",
          color: "white",
          borderRadius: 8,
          cursor: "pointer",
        }}
      >
        {inventoryAccessLoading ? (lang === "tr" ? "Kontrol ediliyor..." : "Checking...") : (lang === "tr" ? "Devam Et" : "Continue")}
      </button>

      <div style={{ marginTop: 24 }}>
        <button
          onClick={() => {
            setAdminSifreGirdi("");
            setAdminSifreHata("");
            setAdminPanelAcik(true);
          }}
          style={{
            padding: "10px 16px",
            backgroundColor: "#333",
            color: "white",
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          {t.adminPanel}
        </button>
      </div>
    </div>
  );
}

// ==================== EKRAN 0 — DİL SEÇİMİ (iki dilli) ====================
if (step === 0)
  return (
    <div style={{ textAlign: "center", marginTop: 40 }}>
      <h2 style={{ marginBottom: 2 }}>{i18n.tr.welcome}</h2>
      <h3 style={{ marginTop: 0, fontWeight: 600, color: "#555" }}>{i18n.en.welcome}</h3>
      <p style={{ marginBottom: 2 }}>{i18n.tr.chooseLang}</p>
      <p style={{ marginTop: 0, color: "#555" }}>{i18n.en.chooseLang}</p>

      <div
        style={{
          maxWidth: 520,
          margin: "16px auto",
          padding: "12px 16px",
          borderRadius: 12,
          background: "#fff7ed",
          border: "1px solid #fed7aa",
          color: "#7c2d12",
          fontSize: 14,
          lineHeight: 1.5,
        }}
      >
        <div>{i18n.tr.warningNote}</div>
        <div style={{ marginTop: 8, color: "#9a3412" }}>{i18n.en.warningNote}</div>
      </div>

      {/* 2026-08-14: Süre notu bu ekrana da eklendi.
          Hazır link (?slAccessCode=...) ile gelen katılımcı erişim kodu ekranını
          ATLADIĞI için oradaki "8-10 dakika" bilgisini hiç görmüyordu. Dil henüz
          seçilmediğinden yukarıdaki uyarı kutusuyla aynı desende iki dilli gösteriliyor. */}
      <div style={{ fontSize: 13, opacity: 0.7, lineHeight: 1.5, margin: "0 auto 4px", maxWidth: 520 }}>
        <div>{i18n.tr.durationNote}</div>
        <div style={{ color: "#555" }}>{i18n.en.durationNote}</div>
      </div>

      <div style={{ display: "flex", gap: 12, justifyContent: "center", margin: "12px 0 20px" }}>
        <button
          onClick={() => { setLang("tr"); setStep(1); }}
          style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #ddd", cursor: "pointer" }}
        >
          Türkçe
        </button>
        <button
          onClick={() => { setLang("en"); setStep(1); }}
          style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #ddd", cursor: "pointer" }}
        >
          English
        </button>
      </div>

      <div style={{ marginTop: 16 }}>
        <button
          onClick={() => {
            setAdminSifreGirdi("");
            setAdminSifreHata("");
            setAdminPanelAcik(true);
          }}
          style={{
            padding: "10px 16px",
            backgroundColor: "#333",
            color: "white",
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          {t.adminPanel}
        </button>
      </div>
    </div>
  );

// ==================== EKRAN 1 — KVKK + OPSİYONEL KATILIMCI BİLGİLERİ ====================
if (step === 1) {
  const generationOptions = [
    { value: "", tr: "Belirtmek istemiyorum", en: "Prefer not to say" },
    { value: "before_1965", tr: "1964 ve öncesi", en: "1964 and earlier" },
    { value: "gen_x_1965_1980", tr: "1965–1980", en: "1965–1980" },
    { value: "gen_y_1981_1996", tr: "1981–1996", en: "1981–1996" },
    { value: "gen_z_1997_2012", tr: "1997–2012", en: "1997–2012" },
  ];

  const roleOptions = [
    { value: "", tr: "Belirtmek istemiyorum", en: "Prefer not to say" },
    { value: "executive", tr: "Üst Yönetim / C-Level / Genel Müdür", en: "Executive / C-Level / General Manager" },
    { value: "director", tr: "Direktör / Fonksiyon Lideri", en: "Director / Functional Leader" },
    { value: "manager", tr: "Müdür", en: "Manager" },
    { value: "team_leader", tr: "Takım Lideri / Supervisor", en: "Team Leader / Supervisor" },
    { value: "specialist", tr: "Uzman / Bireysel Katkı Sağlayan", en: "Specialist / Individual Contributor" },
    { value: "operations", tr: "Operasyon / Saha Ekibi", en: "Operations / Frontline Team" },
    { value: "student", tr: "Öğrenci", en: "Student" },
    { value: "other", tr: "Diğer", en: "Other" },
  ];

  const privacyText =
    lang === "tr"
      ? "Someren Labs, bu envanter kapsamında verdiğiniz yanıtları ve isteğe bağlı olarak paylaştığınız katılımcı bilgilerini; bireysel sonuç üretmek, şirket/takım bazlı toplu analiz yapmak, gelişim raporları oluşturmak ve kullanım/kota kaydını yönetmek amacıyla işler. Ad, soyad, takma ad, rol seviyesi ve jenerasyon aralığı alanları isteğe bağlıdır. Bu alanları boş bırakarak da envantere devam edebilirsiniz. Takma ad ile katılım, gerçek kimlik bilgisinin paylaşılmaması anlamına gelir; ancak sistemde erişim kodu, şirket/paket bilgisi ve kullanım kaydı teknik olarak tutulabilir."
      : "Someren Labs processes your inventory answers and any optional participant information you provide to generate individual results, produce aggregated company/team analysis, create development reports and manage access/quota records. Name, surname, alias, role level and generation range fields are optional. You may continue without filling these fields. Joining with an alias means you do not share your real identity information; however, access code, company/package information and usage records may still be technically stored in the system.";

  const consentText =
    lang === "tr"
      ? "Aydınlatma metnini okudum. Envanter yanıtlarımın ve isteğe bağlı paylaştığım bilgilerin bireysel sonuç, toplu şirket/takım analizi, gelişim raporu ve kullanım/kota takibi amacıyla işlenmesine açık rıza veriyorum."
      : "I have read the privacy notice. I give my explicit consent for my inventory answers and any optional participant information I provide to be processed for individual results, aggregated company/team analysis, development reporting and access/quota tracking.";

  return (
    <div style={{ maxWidth: 760, margin: "28px auto", padding: 16 }}>
      <div
        style={{
          background: "#fff",
          color: "#111827",
          border: "1px solid #e5e7eb",
          borderRadius: 20,
          padding: 24,
          boxShadow: "0 14px 32px rgba(15, 23, 42, 0.08)",
        }}
      >
        <p
          style={{
            margin: "0 0 8px",
            letterSpacing: "0.12em",
            fontSize: 12,
            fontWeight: 800,
            color: "#92400e",
            textAlign: "center",
          }}
        >
          {lang === "tr" ? "KATILIMCI BİLGİLERİ" : "PARTICIPANT INFORMATION"}
        </p>

        <h2 style={{ margin: "0 0 10px", textAlign: "center", color: "#111827" }}>
          {lang === "tr" ? "KVKK ve İsteğe Bağlı Bilgiler" : "Privacy Notice and Optional Information"}
        </h2>

        <p style={{ margin: "0 0 8px", textAlign: "center", color: "#374151", lineHeight: 1.5 }}>
          {lang === "tr"
            ? "Aşağıdaki alanlar isteğe bağlıdır. Devam etmek için KVKK / açık rıza onayı gereklidir."
            : "The fields below are optional. Privacy / explicit consent approval is required to proceed."}
        </p>

        {/* 2026-08-14: Süre notu burada da gösteriliyor — "Başla"ya basmadan hemen önceki
            son ekran. Hazır linkle gelen katılımcı erişim kodu ekranını atladığı için
            bu bilgiyi başka yerde görmüyordu. Dil seçili olduğundan tek dilde. */}
        <p style={{ margin: "0 0 16px", textAlign: "center", color: "#6b7280", fontSize: 13 }}>
          {t.durationNote}
        </p>

        {somerenAccessContext && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 10,
              marginBottom: 14,
              padding: "12px 14px",
              borderRadius: 14,
              background: "#f8fafc",
              border: "1px solid #e5e7eb",
              color: "#111827",
              fontSize: 14,
            }}
          >
            <div>
              <strong>{lang === "tr" ? "Şirket:" : "Company:"}</strong>{" "}
              <span>{somerenAccessContext.companyName || "-"}</span>
            </div>
            <div>
              <strong>{lang === "tr" ? "Takım:" : "Team:"}</strong>{" "}
              <span>{somerenAccessContext.teamName || "-"}</span>
            </div>
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 12,
          }}
        >
          <label style={{ display: "grid", gap: 6, fontWeight: 800, color: "#111827" }}>
            {lang === "tr" ? "Ad / Takma Ad (isteğe bağlı):" : "Name / Alias (optional):"}
            <input
              type="text"
              value={user.name}
              onChange={(e) => setUser({ ...user, name: e.target.value })}
              style={{
                padding: "11px 12px",
                borderRadius: 12,
                border: "1px solid #cbd5e1",
                color: "#111827",
                background: "#fff",
              }}
            />
          </label>

          <label style={{ display: "grid", gap: 6, fontWeight: 800, color: "#111827" }}>
            {lang === "tr" ? "Soyad (isteğe bağlı):" : "Surname (optional):"}
            <input
              type="text"
              value={user.surname}
              onChange={(e) => setUser({ ...user, surname: e.target.value })}
              style={{
                padding: "11px 12px",
                borderRadius: 12,
                border: "1px solid #cbd5e1",
                color: "#111827",
                background: "#fff",
              }}
            />
          </label>

          <label style={{ display: "grid", gap: 6, fontWeight: 800, color: "#111827" }}>
            {lang === "tr" ? "Jenerasyon / Doğum yılı aralığı (isteğe bağlı):" : "Generation / Birth-year range (optional):"}
            <select
              value={user.generationRange}
              onChange={(e) => setUser({ ...user, generationRange: e.target.value })}
              style={{
                padding: "11px 12px",
                borderRadius: 12,
                border: "1px solid #cbd5e1",
                color: "#111827",
                background: "#fff",
              }}
            >
              {generationOptions.map((option) => (
                <option key={option.value || "empty"} value={option.value}>
                  {option[lang]}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: "grid", gap: 6, fontWeight: 800, color: "#111827" }}>
            {lang === "tr" ? "Rol seviyesi (isteğe bağlı):" : "Role level (optional):"}
            <select
              value={user.roleLevel}
              onChange={(e) => setUser({ ...user, roleLevel: e.target.value })}
              style={{
                padding: "11px 12px",
                borderRadius: 12,
                border: "1px solid #cbd5e1",
                color: "#111827",
                background: "#fff",
              }}
            >
              {roleOptions.map((option) => (
                <option key={option.value || "empty"} value={option.value}>
                  {option[lang]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div
          style={{
            marginTop: 14,
            border: "1px solid #e5e7eb",
            borderRadius: 16,
            background: "#f8fafc",
            padding: 14,
            color: "#111827",
          }}
        >
          <h3 style={{ margin: "0 0 8px", color: "#111827" }}>
            {lang === "tr" ? "Aydınlatma Metni" : "Privacy Notice"}
          </h3>
          <p style={{ margin: "0 0 12px", fontSize: 13, lineHeight: 1.5, color: "#334155" }}>
            {privacyText}
          </p>

          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontWeight: 800, color: "#111827", fontSize: 13, lineHeight: 1.45 }}>
            <input
              type="checkbox"
              checked={user.kvkkConsent}
              onChange={(e) => setUser({ ...user, kvkkConsent: e.target.checked })}
              style={{ width: 18, height: 18, marginTop: 2 }}
            />
            <span>{consentText}</span>
          </label>
        </div>

        <button
          onClick={async () => {
            if (!user.kvkkConsent) {
              alert(
                lang === "tr"
                  ? "Devam etmek için KVKK / açık rıza onayı gereklidir."
                  : "Privacy / explicit consent approval is required to continue."
              );
              return;
            }

            if (user.name && user.surname) {
              try {
                setCheckingUser(true);
                const qSnap = await getDocs(
                  query(
                    collection(db, "disc_sonuclari"),
                    where("name", "==", user.name),
                    where("surname", "==", user.surname)
                  )
                );
                if (!qSnap.empty) {
                  const again = window.confirm(t.duplicateConfirm);
                  if (!again) {
                    setCheckingUser(false);
                    return;
                  }
                }
              } catch (_e) {
                // Sessiz geç (offline vb. durumlarda engellemesin)
              } finally {
                setCheckingUser(false);
              }
            }

            setStep(2);
          }}
          style={{
            width: "100%",
            marginTop: 14,
            padding: "13px 18px",
            backgroundColor: "#111827",
            color: "white",
            border: "0",
            borderRadius: 14,
            cursor: "pointer",
            fontWeight: 800,
          }}
        >
          {checkingUser ? (lang === "tr" ? "Kontrol ediliyor..." : "Checking...") : t.start}
        </button>
      </div>
    </div>
  );
}

// ==================== EKRAN 2 — 24×4 TEST ====================
if (step === 2) {
  const group = groups[lang][currentGroup];
  const currentAnswer = answers.find((a) => a.groupId === group.id);

  const canGoNext =
    currentAnswer &&
    typeof currentAnswer.most === "number" &&
    typeof currentAnswer.least === "number";

  const progress = Math.round(((currentGroup + 1) / groups[lang].length) * 100);

  return (
    <div style={{ textAlign: "center", padding: 16, maxWidth: 560, margin: "0 auto" }}>
      <h3>
        {t.group} {group.id}
      </h3>

      {/* Progress Bar */}
      <div
        style={{
          height: 10,
          width: "100%",
          backgroundColor: "#ddd",
          borderRadius: 8,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: "100%",
            backgroundColor: "#4caf50",
            borderRadius: 8,
            transition: "width 200ms ease",
          }}
        />
      </div>

      {/* 4 satır */}
      {group.rows.map((rowText, rIdx) => (
        <div
          key={rIdx}
          style={{
            border: "1px solid #ccc",
            borderRadius: 8,
            padding: 12,
            textAlign: "left",
            marginBottom: 16,
            background: "#fff",
          }}
        >
          <div>{rowText}</div>

          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button
              onClick={() => setRowSelection(group.id, "most", rIdx)}
              style={{
                flex: 1,
                padding: 8,
                backgroundColor: isRowSelected(group.id, "most", rIdx)
                  ? "#1b5e20"
                  : "#4CAF50",
                color: "white",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              {t.most} {isRowSelected(group.id, "most", rIdx) && "✅"}
            </button>

            <button
              onClick={() => setRowSelection(group.id, "least", rIdx)}
              disabled={isRowSelected(group.id, "most", rIdx)}
              style={{
                flex: 1,
                padding: 8,
                backgroundColor: isRowSelected(group.id, "least", rIdx)
                  ? "#b71c1c"
                  : "#f44336",
                color: "white",
                borderRadius: 6,
                cursor: isRowSelected(group.id, "most", rIdx) ? "not-allowed" : "pointer",
                opacity: isRowSelected(group.id, "most", rIdx) ? 0.7 : 1,
              }}
            >
              {t.least} {isRowSelected(group.id, "least", rIdx) && "❌"}
            </button>
          </div>
        </div>
      ))}

      {/* İleri / Sonuçlar */}
      <button
        onClick={() => {
          if (!canGoNext) {
            alert(t.mustSelect);
            return;
          }
          if (currentGroup === groups[lang].length - 1) {
            handleSubmit();
          } else {
            setCurrentGroup(currentGroup + 1);
          }
        }}
        disabled={gonderiliyor}
        style={{
          padding: "10px 18px",
          backgroundColor: "#1976d2",
          color: "white",
          borderRadius: 8,
          width: "100%",
          maxWidth: 240,
          cursor: gonderiliyor ? "default" : "pointer",
          opacity: gonderiliyor ? 0.6 : 1,
        }}
      >
        {gonderiliyor
          ? (lang === "tr" ? "Kaydediliyor…" : "Saving…")
          : currentGroup === groups[lang].length - 1
          ? t.seeResults
          : t.next}
      </button>

      <p style={{ marginTop: 12 }}>
        {currentGroup + 1} {t.progressSep} {groups[lang].length}
      </p>

      <p style={{ fontSize: 13, opacity: 0.7, marginTop: 4 }}>{t.durationNote}</p>
    </div>
  );
}

// ==================== EKRAN 3 — SONUÇ GRAFİKLERİ + KİŞİSEL RAPOR ====================
if (step === 3) {
  const adaptedScores = Object.fromEntries(
    LABELS.map((label, index) => [
      label,
      Math.round(charts?.mostData?.datasets?.[0]?.data?.[index] ?? 0),
    ])
  );

  const naturalScores = Object.fromEntries(
    LABELS.map((label, index) => [
      label,
      Math.round(charts?.leastData?.datasets?.[0]?.data?.[index] ?? 0),
    ])
  );

  return (
    <div style={{ padding: 16, maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
      <h2>{t.resultsTitle}</h2>

      <p>
        <b>{[user.name, user.surname].filter(Boolean).join(" ") || (lang === "tr" ? "Katılımcı" : "Participant")}</b>
        <br />
        <small>{timestamp}</small>
        {durationMs != null && (
          <>
            <br />
            <small>⏱ {t.durationLabel}: {formatDuration(durationMs, lang)}</small>
          </>
        )}
      </p>

      {charts && (
        <>
          {/* %50 ve üzerindeki sinyaller — solda uyarlanmış, sağda doğal.
              Admin'in "Katılımcı PDF" çıktısında da BİREBİR aynısı basılır. */}
          <div style={{ maxWidth: 900, margin: "18px auto 0", textAlign: "left" }}>
            <SinyalPanosu adapted={adaptedScores} natural={naturalScores} tr={lang !== "en"} />
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 24,
            }}
          >
            <div style={{ flex: "1 1 460px", minWidth: 300, height: 360 }}>
              <Bar data={charts.mostData} options={chartOptions(t.adaptedTitle)} />
            </div>

            <div style={{ flex: "1 1 460px", minWidth: 300, height: 360 }}>
              <Bar data={charts.leastData} options={chartOptions(t.naturalTitle)} />
            </div>
          </div>

          {/* 2026-08-18 · Katılımcıya ARTIK detaylı rapor gösterilmiyor.
              Ekranda yalnız kimlik/süre bilgisi ve uyarlanmış + doğal dikey çubuk
              grafikleri kalır. Yorum, örüntü, gelişim planı vb. bölümler yalnız
              Someren Labs tarafındaki raporda yer alır. */}
        </>
      )}
    </div>
  );
}

return null;
}
